import { useEffect, useState } from "react";
import type { BrowseTrack } from "@touchscreen/api-client";
import { useTouchscreenContext, useSearch, searchEmptyStateMessage } from "@touchscreen/hooks";
import { TrackRow } from "../components/TrackRow.js";
import { LikeButton } from "../components/LikeButton.js";
import { RequestSheetHost } from "../components/RequestSheetHost.js";
import { artworkUrl } from "../lib/artwork.js";
import "./SearchScreen.css";

export function SearchScreen(): React.JSX.Element {
  const { venue } = useTouchscreenContext();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTrack, setSelectedTrack] = useState<BrowseTrack | null>(null);

  const searchEnabled = venue?.search.enabled ?? true;
  const strategy = venue?.search.strategy ?? "catalogue";

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const { data: page, isFetching: loading } = useSearch(debouncedQuery, { enabled: searchEnabled, perPage: 30 });
  const results = debouncedQuery.trim() ? page?.items ?? null : null;

  if (!searchEnabled) {
    return (
      <div className="search-screen search-screen--message">
        <p>Search isn't available at this venue.</p>
      </div>
    );
  }

  return (
    <div className="search-screen">
      <input
        className="search-screen__input"
        type="search"
        inputMode="search"
        placeholder="Search for a track or artist"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      {loading ? <p className="search-screen__hint">Searching…</p> : null}
      {!loading && results !== null && results.length === 0 ? (
        <p className="search-screen__hint">{searchEmptyStateMessage(strategy)}</p>
      ) : null}

      <div className="search-screen__list">
        {(results ?? []).map((track) => (
          <TrackRow
            key={track.sm_id}
            title={track.title}
            artist={track.artist}
            artworkUrl={track.artwork_ref ?? artworkUrl(track.sm_id)}
            onClick={() => setSelectedTrack(track)}
            trailing={<LikeButton smId={track.sm_id} title={track.title} artist={track.artist} />}
          />
        ))}
      </div>

      {selectedTrack ? <RequestSheetHost track={selectedTrack} onClose={() => setSelectedTrack(null)} /> : null}
    </div>
  );
}
